<script lang="ts">
	import CardBadge from '$lib/components/ui/CardBadge.svelte';
	import ImageHoverGallery from '$lib/components/ui/ImageHoverGallery.svelte';
	import NativeText from '$lib/components/ui/NativeText.svelte';
	import { CITY_FEATURE_TYPE_SINGLE_LABELS, CityFeatureType } from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { Language } from '$lib/features/country/language/language.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import type { NavLink } from '$lib/utils/url.util';
	import type { Component } from 'svelte';
	import IconArrowRight from '~icons/mdi/arrow-right';

	const {
		headingTag = 'h3',
		elementId,
		englishName,
		nativeName,
		itemType,
		description,
		language,
		images,
		nav,
		badge,
		NoImagePlaceholderIcon,
		eagerLoad = false
	}: {
		headingTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5';
		elementId: string;
		englishName: string;
		nativeName: string | null;
		description: string;
		itemType?: CityFeatureType;
		language: Language;
		images: CoverImage[];
		nav: NavLink;
		badge?: { text: string; Icon: Component | undefined };
		NoImagePlaceholderIcon: Component;
		includeFeatureType?: boolean;
		eagerLoad?: boolean;
	} = $props();
</script>

<a
	id={elementId}
	class="group card h-full scroll-mt-20 overflow-hidden border border-base-content/5 bg-base-200 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
	title="View more about {englishName}"
	href={`/${nav.href}`}
	data-sveltekit-preload-data={nav.preloadData}
	data-sveltekit-preload-code={nav.preloadCode}
>
	<figure class="relative aspect-video w-full overflow-hidden">
		{@render Image()}
	</figure>

	<div class="card-body flex-1 gap-2 p-6">
		<svelte:element this={headingTag} class="card-title line-clamp-1 transition-colors group-hover:text-primary">
			{englishName}
		</svelte:element>

		{#if nativeName}
			<i>
				<NativeText
					classes="text-sm text-base-content/70"
					languageCode={language.code}
					isLanguageRTL={language.rtl}
					text={nativeName}
				/>
			</i>
		{/if}

		<p class="text-md line-clamp-3 grow-0 text-base-content/70">
			{description}
		</p>

		<div
			class={{
				'mt-auto card-actions items-center border-t border-base-content/5 pt-4': true,
				'justify-between': itemType,
				'justify-end': !itemType
			}}
		>
			{#if itemType}
				<span class="font-mono text-xs font-light tracking-widest text-base-content/70">
					{CITY_FEATURE_TYPE_SINGLE_LABELS[itemType].toLowerCase()}
				</span>
			{/if}
			<span class="inline-flex items-center gap-1 font-mono text-sm font-semibold text-primary">
				view <IconArrowRight class="transition-transform group-hover:translate-x-1" />
			</span>
		</div>
	</div>
</a>

{#snippet Image()}
	{#if images.length > 0}
		<ImageHoverGallery {images} eagerLoading={eagerLoad} />
	{:else}
		<div
			class="absolute inset-0 flex flex-col items-center justify-center bg-base-200 text-base-content/20 transition-colors group-hover:text-primary/40"
		>
			<NoImagePlaceholderIcon class="h-12 w-12" />
		</div>
	{/if}
	{#if badge}
		<div class="absolute right-3 bottom-3">
			<CardBadge text={badge.text} Icon={badge.Icon} />
		</div>
	{/if}
{/snippet}
